// src/App.js
import React, { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Login from "./pages/Login";
import Learn from "./pages/Learn";
import Signup from "./pages/Signup";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import Landing from "./pages/Landing";
import PublicRoute from "./components/PublicRoute";
import LanguageGroups from "./pages/LanguageGroups";
import ClassesUser from "./pages/user/ClassesUser";
import GroupsUser from "./pages/user/GroupsUser";
import Unauthorized from "./pages/Unauthorized";
import GroupDetailUser from "./pages/user/GroupDetailUser";
import ForgotPassword from "./pages/ForgotPassword";
import LearnLanguageUser from "./pages/user/LearnLanguageUser";
import AddGroupsUser from "./pages/user/AddGroupsUser";
import ProfileUser from "./pages/user/ProfileUser";
import UserEditProfile from "./pages/user/UserEditProfile";
import UserSettings from "./pages/user/UserSettings";
import SuperTutorUser from "./pages/user/SuperTutorUser";
import MessagesUser from "./pages/user/MessagesUser";
import Splash from "./pages/Splash";
import LanguageExpertsUser from "./pages/user/LanguageExpertsUser";
import SavedRecourcesUser from "./pages/user/SavedRecourcesUser";
import PrivacyPolicyUser from "./pages/user/PrivacyPolicyUser";
import AboutBambuuUser from "./pages/user/AboutBambuuUser";
import LoginTutor from "./pages/LoginTutor";
import ProfileTutor from "./pages/tutor/ProfileTutor";
import EditProfileTutor from "./pages/tutor/EditProfileTutor";
import TutorSettings from "./pages/tutor/SettingsTutor";
import PrivacyPolicyTutor from "./pages/tutor/PrivacyPolicyTutor";
import AboutBambuuTutor from "./pages/tutor/AboutBambuuTutor";
import SavedResourcesTutor from "./pages/tutor/SavedResourcesTutor";
import StudentsTutor from "./pages/tutor/StudentsTutor";
import InstructorProfileUser from "./pages/user/InstructorProfileUser";
import BecomeAnExpertUser from "./pages/user/BecomeAnExpertUser";
import AddClassTutor from "./pages/tutor/AddClassTutor";
import ClassesTutor from "./pages/tutor/ClassesTutor";
import GroupsTutor from "./pages/tutor/GroupsTutor";
import GroupDetailsTutor from "./pages/tutor/GroupDetailsTutor";
import AddGroupsTutor from "./pages/tutor/AddGroupsTutor";
import { Chat } from "stream-chat-react";
import GroupDetailsNotJoinedUser from "./pages/user/GroupDetailsNotJoinedUser";
import ClassDetailsUser from "./pages/user/ClassDetailsUser";
import ClassDetailsNotJoinedUser from "./pages/user/ClassDetailsNotJoinedUser";
import ClassDetailsTutor from "./pages/tutor/ClassDetailsTutor";
import ExploreClassesUser from "./pages/user/ExploreClassesUser";
import ExploreGroupsUser from "./pages/user/ExploreGroupsUser";
import VideoCall from "./pages/user/VideoCall";
import BammbuuPlusGroupsUser from "./pages/user/BammbuuPlusGroupsUser";
import SignupSplash from "./pages/SignupSplash";
import EditGroupsUser from "./pages/user/EditGroupUser";
import EditGroupsTutor from "./pages/tutor/EditGroupTutor";
import EditClassPage from "./pages/tutor/EditClassDetails";
import ProfileSetup from "./pages/user/ProfileSetupUser";
import DeleteAccout from "./pages/user/DeleteAccout";
import TutorDeleteAccount from "./pages/tutor/TutorDeleteAccount";
import { ClassProvider } from "./context/ClassContext";
import VideoCallTutor from "./pages/tutor/VideoCallTutor";
import LanguagesUser from "./pages/user/LanguagesUser";
import { messaging } from "./firebaseConfig";
import { toast } from "react-toastify";
import { onMessage } from "firebase/messaging";
import ContactUs from "./pages/ContactUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import Subscriptions from "./pages/Subscriptions";
import TagManager from "react-gtm-module";
import { initAnalytics, trackPageView } from "./utils/analytics";
import ExamPreparationTutor from "./pages/tutor/ExamPreparationTutor";
import ExamPreparationUser from "./pages/user/ExamPreparationUser";
import MobileConfirmationStep from "./components/mobile-flow/MobileConfirmationStep";
import LandingExamPrep from "./pages/LandingExamPrep";
import ScholarshipsLanding from "./pages/ScholarshipsLanding";
import FluencyChallengeLanding from "./pages/FluencyChallengeLanding";
import FluencyChallengeSuccess from "./pages/FluencyChallengeSuccess";
import USNursesLanding from "./pages/USNursesLanding";
import WebSubscriptionStep from "./pages/user/WebSubscriptionStep";
import ThankYou from "./pages/ThankYou";
import FreeConsultation from "./pages/FreeConsultation";
import LandingInterviewXcelerator from "./pages/LandingInterviewXcelerator";
import FreeConsultationInterview from "./pages/FreeConsultationInterview";

const App = () => {
  const { streamClient } = useAuth(); // Use useAuth() inside the component
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Tag Manager
    const tagManagerArgs = {
      gtmId: "GTM-KZLTKLPJ",
    };
    TagManager.initialize(tagManagerArgs);

    // Initialize our custom analytics
    initAnalytics();
  }, []);

  // Track page views when route changes
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      if (payload.notification) {
        const { title, body } = payload.notification;

        toast.info(`${title} - ${body}`);
      }
    });

    // Clean up the listener on unmount
    return () => unsubscribe();
  }, []);

  if (!streamClient) {
    return <div>Loading...</div>;
  }

  return (
    <LanguageProvider>
      <Chat client={streamClient}>
        <ClassProvider>
          <div className="font-urbanist">
            <ToastContainer position="top-right" autoClose={3000} />

            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Landing />
                  </PublicRoute>
                }
              />
              <Route
                path="/welcome"
                element={
                  <PublicRoute>
                    <Splash />
                  </PublicRoute>
                }
              />
              <Route
                path="/exam-prep"
                element={
                  <PublicRoute>
                    <LandingExamPrep />
                  </PublicRoute>
                }
              />
              <Route
              path="/interview-accelerator"
              element={
                <PublicRoute>
                  <LandingInterviewXcelerator/>
                </PublicRoute>
              }
              />
              <Route
                path="/exam-prep/scholarships"
                element={
                  <PublicRoute>
                    <ScholarshipsLanding />
                  </PublicRoute>
                }
              />
              <Route
                path="/fluency-challenge"
                element={
                  <PublicRoute>
                    <FluencyChallengeLanding />
                  </PublicRoute>
                }
              />

              <Route
                path="/exam-prep/nurses"
                element={
                  <PublicRoute>
                    <USNursesLanding />
                  </PublicRoute>
                }
              />

              <Route
                path="/7-day-success"
                element={
                  <PublicRoute>
                    <FluencyChallengeSuccess />                  </PublicRoute>
                }
              />

              <Route
                path="/thank-you"
                element={
                  <PublicRoute>
                    <ThankYou />
                  </PublicRoute>
                }
              />

              <Route
                path="/free-consultation"
                element={
                  <PublicRoute>
                    <FreeConsultation />
                  </PublicRoute>
                }
              />

              <Route
               path="/interview-success"
               element={
                <PublicRoute>
                  <FreeConsultationInterview/>
                </PublicRoute>
               }
              />

              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsConditions />}
              />
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              <Route
                path="/login-tutor"
                element={
                  <PublicRoute>
                    <LoginTutor />
                  </PublicRoute>
                }
              />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/learn"
                element={
                  <ProtectedRoute requiredRole={["student", "tutor"]}>
                    <Learn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/languageGroups"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguageGroups />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/superTutorUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SuperTutorUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/messagesUser/:channelId?"
                element={
                  <ProtectedRoute requiredRole="student">
                    <MessagesUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/studentsTutor/:channelId?"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <StudentsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/languageExpertsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguageExpertsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addClassTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AddClassTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/:tutorId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <InstructorProfileUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/becomeAnExpert"
                element={
                  <ProtectedRoute requiredRole="student">
                    <BecomeAnExpertUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/savedRecourcesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SavedRecourcesUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute requiredRole={["student", "tutor"]}>
                    <Subscriptions />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subscribe-exam-prep"
                element={
                  <ProtectedRoute requiredRole="student">
                    <WebSubscriptionStep />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/savedRecourcesTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <SavedResourcesTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/examPreparation"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ExamPreparationTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profileUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ProfileUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profileTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ProfileTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/userEditProfile"
                element={
                  <ProtectedRoute requiredRole="student">
                    <UserEditProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutorEditProfile"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <EditProfileTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/userSettings"
                element={
                  <ProtectedRoute requiredRole="student">
                    <UserSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutorSettings"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <TutorSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/privacyPolicyUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <PrivacyPolicyUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/privacyPolicyTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <PrivacyPolicyTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aboutBambuuUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <AboutBambuuUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aboutBambuuTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AboutBambuuTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classDetailsUser/:classId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassDetailsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classDetailsTutor/:classId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ClassDetailsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/newClassDetailsUser/:classId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ClassDetailsNotJoinedUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classesTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <ClassesTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exploreClassesUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ExploreClassesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exploreGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ExploreGroupsUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupsUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupsTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <GroupsTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/groupDetailsUser/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupDetailUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/newGroupDetailsUser/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    <GroupDetailsNotJoinedUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groupDetailsTutor/:groupId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <GroupDetailsTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <AddGroupsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addGroupsTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <AddGroupsTutor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute requiredRole="student">
                    <SignupSplash />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-setup"
                element={
                  <ProtectedRoute requiredRole="student">
                    <ProfileSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learnLanguageUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LearnLanguageUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/languages"
                element={
                  <ProtectedRoute requiredRole="student">
                    <LanguagesUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bammbuuPlusGroupsUser"
                element={
                  <ProtectedRoute requiredRole="student">
                    <BammbuuPlusGroupsUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call"
                element={
                  <ProtectedRoute requiredRole="student">
                    <VideoCall />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/callTutor"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    <VideoCallTutor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editGroup/:groupId"
                element={
                  <ProtectedRoute requiredRole="student">
                    {<EditGroupsUser />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editGroupTutor/:groupId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    {<EditGroupsTutor />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-class/:classId"
                element={
                  <ProtectedRoute requiredRole="tutor">
                    {<EditClassPage />}
                  </ProtectedRoute>
                }
              />

              <Route
                path="/deleteAccountTutor"
                element={
                  <ProtectedRoute requiredRole={["tutor"]}>
                    <TutorDeleteAccount />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/deleteAccount"
                element={
                  <ProtectedRoute requiredRole={["student"]}>
                    <DeleteAccout />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/examPreparationUser/:tutorId"
                element={
                  <ProtectedRoute requiredRole={["student"]}>
                    <ExamPreparationUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/mobile-confirmation"
                element={<MobileConfirmationStep />}
              />
            </Routes>
          </div>
        </ClassProvider>
      </Chat>
    </LanguageProvider>
  );
};

export default App;
